import React, { useEffect, useState, useRef } from 'react';
import { Radiation } from 'lucide-react';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';

export default function NuclearStrikeAnimation({ sourceCode, targetCode, sourcePos, targetPos, success = true, onComplete }) {
  const [phase, setPhase] = useState('launch'); // launch, flying, explosion, complete
  const [nukePosition, setNukePosition] = useState({ x: sourcePos.x, y: sourcePos.y });
  const [mushroomCloudImage, setMushroomCloudImage] = useState('');
  const [targetLeaderPosition, setTargetLeaderPosition] = useState(50); // Horizontal position (%)
  const flyingSoundRef = useRef(null);
  const alarmRef = useRef(null);
  const blastRef = useRef(null);
  const aftermathRef = useRef(null);
  const hasInitialized = useRef(false); // Prevent double execution

  useEffect(() => {
    // CRITICAL FIX: Only run animation ONCE, even if props change
    if (hasInitialized.current) {
      console.log('⚠️ Nuclear animation already initialized, skipping duplicate');
      return;
    }
    
    hasInitialized.current = true;
    console.log('🚀 Starting nuclear strike animation');
    
    // 🔈 Claim the audio channel so iOS hands us the slot for alarm/blast SFX
    claimAudioChannel();
    
    // Select random mushroom cloud image
    const cloudImages = [
      '/assets/explosions/mushroom-cloud-1.png',
      '/assets/explosions/mushroom-cloud-2.png',
      '/assets/explosions/mushroom-cloud-3.png',
      '/assets/explosions/mushroom-cloud-4.png'
    ];
    const randomCloud = cloudImages[Math.floor(Math.random() * cloudImages.length)];
    setMushroomCloudImage(randomCloud);
    
    // Start alarm at 20% volume
    const alarm = new Audio('/assets/audio/nuke-launch-alarm.mp3');
    alarm.volume = 0.2; // ⭐ 20% volume for background ambiance
    alarm.loop = true;
    alarmRef.current = alarm;
    alarm.play().catch(e => console.log('Alarm failed:', e));

    // CINEMATIC LAUNCH SEQUENCE
    const launchTimer = setTimeout(() => {
      setPhase('flying');
      
      // Start flying sound
      const flyingSound = new Audio('/assets/audio/nuke-flying.mp3');
      flyingSound.volume = 0.8;
      flyingSoundRef.current = flyingSound;
      flyingSound.play().catch(e => console.log('Flying sound failed:', e));
      
      // SLOW CINEMATIC FLIGHT: 9 seconds total (removed descent phase)
      const LAUNCH_DURATION = 2000;   // 2s vertical launch
      const TURN_DURATION = 1000;     // 1s rotation
      const FLIGHT_DURATION = 6000;   // 6s horizontal flight directly to target
      const TOTAL_DURATION = LAUNCH_DURATION + TURN_DURATION + FLIGHT_DURATION;
      
      const startTime = Date.now();
      
      const animateNuke = () => {
        const elapsed = Date.now() - startTime;
        const totalProgress = Math.min(elapsed / TOTAL_DURATION, 1);
        
        if (totalProgress < 1) {
          // Phase 1: Vertical Launch (0-20% of animation)
          if (elapsed < LAUNCH_DURATION) {
            const launchProgress = elapsed / LAUNCH_DURATION;
            // Start at ground (y + 10), rise straight up
            const verticalRise = launchProgress * 15; // Rise 15% of screen height
            
            setNukePosition({ 
              x: sourcePos.x, 
              y: sourcePos.y + 10 - verticalRise,
              scale: 0.3 + (launchProgress * 0.7), // Grow from 30% to 100%
              rotation: 0, // Point straight up
              phase: 'launch'
            });
          }
          // Phase 2: Turn to face target (20-30% of animation)
          else if (elapsed < LAUNCH_DURATION + TURN_DURATION) {
            const turnProgress = (elapsed - LAUNCH_DURATION) / TURN_DURATION;
            const angle = Math.atan2(targetPos.y - sourcePos.y, targetPos.x - sourcePos.x) * (180 / Math.PI) + 90;
            
            setNukePosition({
              x: sourcePos.x,
              y: sourcePos.y + 10 - 15, // Stay at apex
              scale: 1,
              rotation: turnProgress * angle, // Gradually rotate to target angle
              phase: 'turning'
            });
          }
          // Phase 3: Horizontal flight all the way to target (27-100% of animation)
          else {
            const flightProgress = (elapsed - LAUNCH_DURATION - TURN_DURATION) / FLIGHT_DURATION;
            
            // Smooth easing - slow start, fast middle, slow end
            const easeProgress = flightProgress < 0.5
              ? 2 * flightProgress * flightProgress
              : 1 - Math.pow(-2 * flightProgress + 2, 2) / 2;
            
            // Calculate position along curved path going ALL THE WAY to target
            const x = sourcePos.x + (targetPos.x - sourcePos.x) * easeProgress;
            const baseY = sourcePos.y + (targetPos.y - sourcePos.y) * easeProgress;
            
            // DRAMATIC ARC following Earth's curvature
            const arcHeight = Math.sin(easeProgress * Math.PI) * 25; // 25% screen height arc!
            const y = baseY - 15 - arcHeight; // Start from apex, add arc
            
            const angle = Math.atan2(targetPos.y - sourcePos.y, targetPos.x - sourcePos.x) * (180 / Math.PI) + 90;
            
            setNukePosition({ 
              x, 
              y,
              scale: 1,
              rotation: angle,
              phase: 'flying'
            });
          }
          
          requestAnimationFrame(animateNuke);
        } else {
          // Nuke reached target - Check if successful
          setPhase(success ? 'explosion' : 'failed');
          flyingSound.pause();
          
          if (success) {
            // SUCCESS - Play blast sound and mushroom cloud
            const blast = new Audio('/assets/audio/nuke-blast.mp3');
            blast.volume = 1.0;
            blastRef.current = blast;
            blast.play().catch(e => console.log('Blast sound failed:', e));
            
            // After blast finishes (~3 seconds), play aftermath sound
            blast.addEventListener('ended', () => {
              const aftermath = new Audio('/assets/audio/nuke-aftermath.mp3');
              aftermath.volume = 0.8;
              aftermathRef.current = aftermath;
              aftermath.play().catch(e => console.log('Aftermath sound failed:', e));
            });
            
            // Mushroom cloud stays visible for 12 seconds total
            setTimeout(() => {
              setPhase('complete');
              alarm.pause();
              if (blastRef.current) blastRef.current.pause();
              if (aftermathRef.current) aftermathRef.current.pause();
              onComplete();
            }, 12000);
          } else {
            // FAILED - Just play a fizzle sound and end quickly
            const fizzle = new Audio('/assets/audio/nuke-blast.mp3');
            fizzle.volume = 0.3; // Quieter failure sound
            fizzle.play().catch(e => console.log('Fizzle sound failed:', e));
            
            // End quickly (3 seconds)
            setTimeout(() => {
              setPhase('complete');
              alarm.pause();
              onComplete();
            }, 3000);
          }
        }
      };
      
      animateNuke();
    }, 1000);
    
    // PANICKING TARGET LEADER ANIMATION - Runs in parallel!
    const panicInterval = setInterval(() => {
      // Random pacing left and right (40% - 60% of screen width)
      const randomPos = 40 + Math.random() * 20;
      setTargetLeaderPosition(randomPos);
    }, 300); // Change position every 300ms for frantic pacing

    return () => {
      clearTimeout(launchTimer);
      clearInterval(panicInterval);
      if (flyingSoundRef.current) {
        flyingSoundRef.current.pause();
      }
      if (alarmRef.current) {
        alarmRef.current.pause();
      }
      if (blastRef.current) {
        blastRef.current.pause();
      }
      if (aftermathRef.current) {
        aftermathRef.current.pause();
      }
      // 🔈 Release audio channel so background music resumes
      releaseAudioChannel();
    };
  }, []); // Empty deps - only run once on mount

  const getNukeSprite = (countryCode) => {
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'eu',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    return `/assets/nukes/nuke-${spriteName}.png`;
  };

  const getScaredLeaderSprite = (countryCode) => {
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'europe',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    return `/assets/leaders/${spriteName}-scared.png`;
  };

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Red alert backdrop */}
      <div className="absolute inset-0 bg-red-900/40 animate-pulse" />
      
      {/* Warning text */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-red-900/90 border-4 border-red-500 px-8 py-4 rounded-lg animate-pulse">
          <div className="flex items-center gap-3">
            <Radiation className="w-8 h-8 text-red-500 animate-spin" />
            <div className="text-red-500 font-mono text-2xl uppercase tracking-wider font-bold">
              ⚠️ NUCLEAR STRIKE DETECTED ⚠️
            </div>
            <Radiation className="w-8 h-8 text-red-500 animate-spin" />
          </div>
        </div>
      </div>
      
      {/* PANICKING TARGET LEADER - Shows during launch/flight, hides during explosion */}
      {(phase === 'launch' || phase === 'flying') && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-50">
          <div 
            className="relative"
            style={{
              transform: `translateX(${targetLeaderPosition - 50}%)`,
              transition: 'transform 0.3s ease-out'
            }}
          >
            {/* Panicking leader sprite with shake animation */}
            <div className="panic-shake">
              <img
                src={getScaredLeaderSprite(targetCode)}
                alt="Panicking Leader"
                className="w-48 h-48 object-contain"
                style={{
                  filter: 'drop-shadow(0 10px 30px rgba(255, 0, 0, 0.8))',
                }}
              />
            </div>
            
            {/* Panic text above leader */}
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <div className="bg-yellow-500 text-black font-bold px-4 py-2 rounded-lg animate-bounce text-lg border-4 border-yellow-600">
                😱 INCOMING MISSILE! 😱
              </div>
            </div>
            
            {/* Sweat drops animation */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2">
              <div className="sweat-drop" style={{ left: '-20px' }}>💦</div>
              <div className="sweat-drop" style={{ left: '20px', animationDelay: '0.3s' }}>💦</div>
            </div>
          </div>
        </div>
      )}

      {/* Flying nuke with cinematic launch, turn, and flight */}
      {(phase === 'launch' || phase === 'flying') && (
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${nukePosition.x}%`,
            top: `${nukePosition.y}%`,
            transform: `translate(-50%, -50%) rotate(${nukePosition.rotation || 0}deg) scale(${nukePosition.scale || 1})`,
            filter: 'drop-shadow(0 0 30px rgba(255, 0, 0, 1))',
            zIndex: 100,
            transition: nukePosition.phase === 'turning' ? 'transform 0.1s linear' : 'none'
          }}
        >
          <img
            src={getNukeSprite(sourceCode)}
            alt="Nuclear Missile"
            className="w-24 h-36 object-contain"
            style={{
              animation: nukePosition.phase === 'launch' ? 'none' : 'pulse 0.5s ease-in-out infinite'
            }}
          />
          {/* Flame trail - show during flight, not launch */}
          {nukePosition.phase === 'flying' && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
              <div className="w-8 h-16 bg-gradient-to-t from-orange-500 via-red-500 to-transparent opacity-80 animate-pulse" />
            </div>
          )}
          
          {/* Smoke trail during launch */}
          {nukePosition.phase === 'launch' && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2">
              <div className="w-12 h-24 bg-gradient-to-b from-gray-400 via-gray-500 to-transparent opacity-60 animate-pulse" />
            </div>
          )}
        </div>
      )}

      {/* Mushroom cloud explosion (only if successful) */}
      {phase === 'explosion' && success && (
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${targetPos.x}%`,
            top: `${targetPos.y}%`,
            zIndex: 100
          }}
        >
          {/* Main explosion flash */}
          <div className="absolute inset-0 w-96 h-96 -mt-48 -ml-48">
            <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-75" />
            <div className="absolute inset-0 bg-orange-500 rounded-full animate-pulse" />
          </div>
          
          {/* Fire and destruction effects - Red/Orange flashing */}
          <div className="absolute inset-0 w-96 h-96 -mt-48 -ml-48">
            {/* Pulsing red fire layer */}
            <div 
              className="absolute inset-0 rounded-full opacity-80"
              style={{
                background: 'radial-gradient(circle, rgba(255,69,0,0.9) 0%, rgba(220,38,38,0.6) 50%, transparent 70%)',
                animation: 'fire-pulse 0.5s ease-in-out infinite'
              }}
            />
            
            {/* Alternating orange glow */}
            <div 
              className="absolute inset-0 rounded-full opacity-70"
              style={{
                background: 'radial-gradient(circle, rgba(255,140,0,0.8) 0%, rgba(249,115,22,0.5) 60%, transparent 80%)',
                animation: 'fire-pulse 0.7s ease-in-out infinite reverse'
              }}
            />
            
            {/* Flickering flames at base */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-64 h-64">
              <div 
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(to top, rgba(255,69,0,0.9), rgba(255,140,0,0.7), transparent)',
                  animation: 'flicker 0.3s ease-in-out infinite'
                }}
              />
            </div>
          </div>
          
          {/* Actual mushroom cloud image - CENTERED */}
          <div className="absolute -mt-32 -ml-32 flex items-center justify-center w-64 h-64">
            <img 
              src={mushroomCloudImage}
              alt="Mushroom Cloud"
              className="w-full h-full object-contain"
              style={{
                filter: 'drop-shadow(0 0 50px rgba(255, 100, 0, 1)) brightness(1.2) contrast(1.3)',
                animation: 'mushroom-rise 2s ease-out'
              }}
            />
          </div>

          {/* Radiation symbol */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-32">
            <Radiation className="w-20 h-20 text-yellow-500 animate-spin" />
          </div>

          {/* Shockwave rings */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-48 h-48 border-4 border-orange-500 rounded-full animate-ping" />
          </div>
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-64 h-64 border-4 border-red-500 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
          </div>
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-80 h-80 border-4 border-yellow-500 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      )}
      
      {/* ❌ FAILED NUKE - Show impact but no mushroom cloud */}
      {phase === 'failed' && (
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${targetPos.x}%`,
            top: `${targetPos.y}%`,
            zIndex: 100
          }}
        >
          {/* Small explosion puff (fizzle) */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-32 h-32 rounded-full bg-gray-500 opacity-70 animate-ping" />
          </div>
          
          {/* INTERCEPTED text */}
          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <div className="bg-red-600 text-white font-bold px-6 py-3 rounded-lg text-2xl border-4 border-red-800 animate-bounce">
              ❌ INTERCEPTED ❌
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes mushroom-rise {
          0% {
            transform: translateY(50px) scale(0.5);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 0.9;
          }
        }
        
        @keyframes fire-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }
        
        @keyframes flicker {
          0%, 100% {
            opacity: 0.9;
          }
          25% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
          75% {
            opacity: 0.7;
          }
        }
        
        /* PANIC ANIMATIONS */
        @keyframes panic-shake {
          0%, 100% {
            transform: translateX(0) rotate(0deg);
          }
          10% {
            transform: translateX(-10px) rotate(-5deg);
          }
          20% {
            transform: translateX(10px) rotate(5deg);
          }
          30% {
            transform: translateX(-10px) rotate(-5deg);
          }
          40% {
            transform: translateX(10px) rotate(5deg);
          }
          50% {
            transform: translateX(-8px) rotate(-3deg);
          }
          60% {
            transform: translateX(8px) rotate(3deg);
          }
          70% {
            transform: translateX(-5px) rotate(-2deg);
          }
          80% {
            transform: translateX(5px) rotate(2deg);
          }
          90% {
            transform: translateX(-3px) rotate(-1deg);
          }
        }
        
        .panic-shake {
          animation: panic-shake 0.5s ease-in-out infinite;
        }
        
        @keyframes sweat-fall {
          0% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(100px) scale(0.5);
            opacity: 0;
          }
        }
        
        .sweat-drop {
          position: absolute;
          top: 20px;
          font-size: 24px;
          animation: sweat-fall 1s ease-in infinite;
        }
      `}</style>
    </div>
  );
}
